import { generateId } from "@fleet/utils";
import { createEffect, createSignal, onCleanup, Show, untrack } from "solid-js";
import { AlarmClockOff, Pause, Play, ShieldCheck } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncSingle } from "@time-tracker/shared/solid";
import { and, eq, time_entry, type TimeEntry } from "@time-tracker/powersync";
import {
  computeIdleState,
  type IdleAction,
  idleMinutesForDisplay,
  liveTimeEntryFilters,
  patchRowVerified,
  queryKeys,
  resolveIdleAction,
  updateUserPresenceMutation,
} from "@time-tracker/shared";
import { idleThresholdMinutes } from "@/features/chronos-sync/idle-threshold";

/** How often the guard re-evaluates idleness while a timer is running. */
const IDLE_CHECK_INTERVAL_MS = 1000;

/** DOM events that count as "the user is here". Cheap passive listeners. */
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;

interface PendingIdle {
  entryId: string;
  idleStartAt: number;
}

/**
 * IDLE DETECTION — always mounted in the chronos layout. While a timer is
 * running and the device-local threshold (Settings) is enabled, it watches for
 * input activity; once the threshold elapses without any, it freezes the idle
 * start and offers to keep the time, discard it (stop at the idle start) or
 * discard it and keep tracking from now (split). The pure math lives in
 * `@time-tracker/shared` (`computeIdleState` / `resolveIdleAction`).
 *
 * Backgrounded tabs throttle `setInterval`, which is fine: the check fires on
 * return and the dialog greets the user with the idle stretch already frozen.
 */
export function IdleGuard() {
  const { userId } = useSession();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const [pendingIdle, setPendingIdle] = createSignal<PendingIdle | null>(null);
  const [isApplying, setIsApplying] = createSignal(false);

  // Same query/key as PulseTimer → shares the reactive cache entry.
  const activeTimer = createPowerSyncSingle<TimeEntry>({
    queryKey: () => queryKeys.chronos.activeTimer(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const currentUserId = userId();
      if (!dbInstance || !currentUserId) {
        return null;
      }
      return dbInstance
        .select()
        .from(time_entry)
        .where(
          and(
            eq(time_entry.user_id, currentUserId),
            eq(time_entry.is_running, 1),
            ...liveTimeEntryFilters(),
          ),
        )
        .limit(1);
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // Track activity + tick the idle check only while it matters: running timer,
  // threshold enabled and no pending dialog. Re-arms (with a fresh baseline)
  // whenever any of those change — e.g. right after the user resolves a dialog.
  createEffect(() => {
    const entry = activeTimer.data;
    const threshold = idleThresholdMinutes();
    if (!entry || threshold <= 0 || pendingIdle() !== null) {
      return;
    }

    let lastActivityAt = Date.now();
    const markActivity = () => {
      lastActivityAt = Date.now();
    };
    for (const evt of ACTIVITY_EVENTS) {
      globalThis.addEventListener(evt, markActivity, { passive: true });
    }

    const intervalId = setInterval(() => {
      const state = computeIdleState({
        lastActivityAt,
        now: Date.now(),
        thresholdMinutes: threshold,
      });
      if (state.isIdle && state.idleStartAt !== null) {
        setPendingIdle({ entryId: entry.id, idleStartAt: state.idleStartAt });
      }
    }, IDLE_CHECK_INTERVAL_MS);

    onCleanup(() => {
      clearInterval(intervalId);
      for (const evt of ACTIVITY_EVENTS) {
        globalThis.removeEventListener(evt, markActivity);
      }
    });
  });

  const idleMinutes = () => {
    const pending = pendingIdle();
    return pending ? idleMinutesForDisplay(Date.now() - pending.idleStartAt) : 0;
  };

  const applyIdleAction = async (action: IdleAction) => {
    const pending = untrack(pendingIdle);
    if (!pending || untrack(isApplying)) {
      return;
    }
    const dbInstance = drizzle();
    const entry = activeTimer.data;
    // Timer already stopped/replaced elsewhere (other tab/device): nothing to fix.
    if (!dbInstance || !entry || entry.id !== pending.entryId) {
      setPendingIdle(null);
      return;
    }
    const resolution = resolveIdleAction(action, {
      entryStartIso: entry.start_time,
      idleStartAt: pending.idleStartAt,
    });
    if (resolution.stopAtIso === null) {
      // Keep the time: the entry is untouched; re-arming happens via the effect.
      setPendingIdle(null);
      return;
    }
    setIsApplying(true);
    try {
      const nowIso = new Date().toISOString();
      await patchRowVerified(dbInstance, time_entry, time_entry.id, entry.id, {
        is_running: 0,
        end_time: resolution.stopAtIso,
      });
      if (resolution.resume) {
        await dbInstance.insert(time_entry).values({
          id: generateId(),
          user_id: entry.user_id ?? userId(),
          project_id: entry.project_id,
          task_id: entry.task_id,
          description: entry.description,
          is_running: 1,
          start_time: nowIso,
          source: "manual",
          created_at: nowIso,
          updated_at: nowIso,
        });
        showToast(t(msg`Idle time discarded — timer restarted`), "success");
      } else {
        // Presence back to online (the timer is no longer running). `user`
        // goes through `updateUserPresenceMutation` (`patchRowVerified`)
        // like every other preference — see `verified-write.ts`.
        await updateUserPresenceMutation(dbInstance, userId(), "online");
        showToast(t(msg`Idle time discarded — timer stopped`), "success");
      }
    } catch (err) {
      console.error("Failed to apply idle resolution", err);
      showToast(t(msg`Action failed`), "error");
    } finally {
      setIsApplying(false);
      setPendingIdle(null);
    }
  };

  return (
    <Dialog
      role="alertdialog"
      open={pendingIdle() !== null}
      onOpenChange={(open) => {
        // Dismissing (Esc/overlay/back) = "keep the time".
        if (!open) {
          setPendingIdle(null);
        }
      }}
      contentClass="max-w-[420px] p-6"
    >
      <div data-testid="idle-dialog">
        <div class="mb-3 flex items-center gap-2 text-warning">
          <AlarmClockOff class="h-5 w-5" />
          <DialogTitle class="text-xl font-bold">{t(msg`Are you still working?`)}</DialogTitle>
        </div>
        <DialogDescription class="text-sm text-muted-foreground mb-6">
          {t(
            msg`Your timer kept running but we detected about {minutes} min without activity. What should we do with that idle time?`,
            { minutes: idleMinutes() },
          )}
        </DialogDescription>
        <div class="flex flex-col gap-2">
          <Button
            data-testid="idle-keep"
            variant="default"
            class="h-11 w-full rounded-xl font-bold flex items-center justify-center gap-2"
            disabled={isApplying()}
            onClick={() => void applyIdleAction("keep")}
          >
            <ShieldCheck class="h-4 w-4" />
            {t(msg`Keep the time`)}
          </Button>
          <Button
            data-testid="idle-discard-resume"
            variant="ghost"
            class="h-11 w-full rounded-xl font-bold border border-border/60 flex items-center justify-center gap-2"
            disabled={isApplying()}
            onClick={() => void applyIdleAction("discard_resume")}
          >
            <Play class="h-4 w-4" />
            {t(msg`Discard idle & keep tracking`)}
          </Button>
          <Button
            data-testid="idle-discard-stop"
            variant="destructive"
            class="h-11 w-full rounded-xl font-bold flex items-center justify-center gap-2"
            disabled={isApplying()}
            onClick={() => void applyIdleAction("discard_stop")}
          >
            <Pause class="h-4 w-4" />
            {t(msg`Discard idle & stop timer`)}
          </Button>
        </div>
        <Show when={isApplying()}>
          <p class="mt-3 text-center text-xs text-muted-foreground">{t(msg`Working...`)}</p>
        </Show>
      </div>
    </Dialog>
  );
}
